import { Mobile } from '@bimser/common';
import {
	BCTreeview,
	ITreeviewExpandEventArgs,
	ITreeviewRightClickEventArgs,
	ITreeviewSelectEventArgs
} from '@bimser/components';
import { FormItemTypes, IDictionary, isNullOrUndefined } from '@bimser/core';
import { Liquid } from 'liquidjs';
import _isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { JSX } from 'react';
import {
	DataSourceType,
	IDataSource,
	IOnLoadArgs,
	IPropertyField
} from '../../../../entities/common';
import { getDataSourceData } from '../../../../helper';
import CompositeDataBoundControl from '../../base/CompositeDataBoundControl';
import { IDataLoadEventArgs } from '../../entities/EventArgs';
import Styles from './assets/styles.scss';
import ITreeviewItem from './entities/ITreeViewItem';
import { ITreeViewProperties } from './entities/ITreeViewProperties';
import { ITreeViewSelectionOptions } from './entities/ITreeViewSelectionOptions';
const liquidEngine = new Liquid();

export default class TreeView extends CompositeDataBoundControl {
	treeItems: ITreeviewItem<any>[];
	selectedItems: ITreeviewItem<any>[];
	showSearch: boolean;
	dataSource: IDataSource;
	dataSourceType: DataSourceType;
	notRecursive?: boolean;
	isMultiLanguageText?: boolean;
	selection: ITreeViewSelectionOptions;
	childrenKey?: string;
	parentKey?: string;
	textSeparator: string;
	valueSeparator: string;
	hierarchyType?: string;
	__expandAll?: boolean;
	hasItemExpr: string;
	__isMobileView: boolean = false;

	__searchValue: string = '';
	__dataSourceLoadedAtLeastOnce?: boolean;
	__data?: any[] = [];

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.TreeView;

		this.treeItems = [];
		this.selectedItems = [];
		this.dataSourceType = 'dynamic';
		this.notRecursive = true;
		this.isMultiLanguageText = true;
		this.childrenKey = '';
		this.parentKey = '';
		this.dataSource = {
			name: '',
			valueExpr: '',
			displayExpr: [],
			displayFormat: '',
			runAtServer: false,
			parameters: [],
			useClientCachedData: true
		};
		this.selection = {
			mode: 'single',
			type: 'rowselect'
		};
		this.textSeparator = ',';
		this.valueSeparator = ',';
		this.hierarchyType = null;

		this.onCheck = this.onCheck.bind(this);
		this.onExpand = this.onExpand.bind(this);
		this.onChangeSelectedItems = this.onChangeSelectedItems.bind(this);
		this.onCollapseTree = this.onCollapseTree.bind(this);
		this.__getSelectedItems = this.__getSelectedItems.bind(this);
		this.__onRightClick = this.__onRightClick.bind(this);
		this.__onSearch = this.__onSearch.bind(this);
		this.__getTreeItemsByRelations = this.__getTreeItemsByRelations.bind(this);
		this.__getIsLeafStatus = this.__getIsLeafStatus.bind(this);
		this.__getItemsMobile = this.__getItemsMobile.bind(this);

		this.subscribe('trigger:OnClick', (value: string) => {
			this.onChangeSelectedItems({
				selectedItems: JSON.parse(value).map(key => ({ key }))
			});
		});
	}

	__loadProperties(data: ITreeViewProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.dataSourceType = data.dataSourceType;

		if (
			!data.selectedItems?.length &&
			!this.__form.documentId &&
			!isEqual(this.treeItems, data.treeItems)
		) {
			const selectedItems: ITreeviewItem<any>[] = [];

			if (this.selection?.mode === 'single' || this.selection?.type === 'rowselect') {
				this.__getSelectedItems(data.treeItems, selectedItems);
			} else if (this.selection?.type === 'checkbox') {
				this.__getCheckedItems(data.treeItems, selectedItems);
			}

			this.__proxyChanged.selectedItems = selectedItems;
		} else {
			this.__proxyChanged.selectedItems = data.selectedItems;
		}

		if (
			(Mobile.MobileView.isAndroidAvaliable || Mobile.MobileView.isIosAvaliable) &&
			this.__isMobileView
		) {
			this.__proxyChanged.treeItems = this.__getItemsMobile(
				data.treeItems,
				this.__proxyChanged.selectedItems,
				this.selection?.type
			);
		} else {
			this.__proxyChanged.treeItems = data.treeItems;
		}

		this.__proxyChanged.showSearch = data.showSearch;
		this.__proxyChanged.notRecursive = data.notRecursive;
		this.__proxyChanged.selection = data.selection;
		this.__proxyChanged.childrenKey = data.childrenKey;
		this.__proxyChanged.parentKey = data.parentKey;
		this.__proxyChanged.textSeparator = data.textSeparator;
		this.__proxyChanged.valueSeparator = data.valueSeparator;
		this.__proxyChanged.hierarchyType = data.hierarchyType;
		this.__proxyChanged.hasItemExpr = data.hasItemExpr;
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			treeItems: this.treeItems,
			selectedItems: this.selectedItems,
			showSearch: this.showSearch,
			dataSource: this.dataSource,
			dataSourceType: this.dataSourceType,
			notRecursive: this.notRecursive,
			isMultiLanguageText: this.isMultiLanguageText,
			selection: this.selection,
			childrenKey: this.childrenKey,
			parentKey: this.parentKey,
			textSeparator: this.textSeparator,
			valueSeparator: this.valueSeparator,
			value: this.selectedItems.map(item => item.key.toString())
		};
	}

	__getPropertyFields(): IPropertyField[] {
		return [
			...super.__getPropertyFields(),
			{
				name: 'textSeparator',
				type: 'string'
			},
			{
				name: 'valueSeparator',
				type: 'string'
			}
		];
	}

	__getChilds(data: Array<any>, parentID: any): Array<any> {
		return data
			.map(i => {
				if (i[this.parentKey] === parentID) {
					return {
						...i,
						children: this.__getChilds(data, i[this.dataSource.valueExpr])
					};
				}
				return null;
			})
			.filter(x => x != null);
	}

	private __convertRecursiveData(data: Array<any>): Array<any> {
		const recursiveData: Array<any> = new Array();
		data.forEach(item => {
			if (
				isNullOrUndefined(item[this.parentKey]) ||
				item[this.parentKey] === '' ||
				item[this.parentKey] === 0
			) {
				recursiveData.push({
					...item,
					children: isNullOrUndefined(item[this.dataSource.valueExpr])
						? []
						: this.__getChilds(data, item[this.dataSource.valueExpr])
				});
			}
		});
		return recursiveData;
	}

	private __getChildsWithKey(data: any) {
		if (this.childrenKey.includes('.')) {
			let _data = typeof data === 'object' && !Array.isArray(data) ? { ...data } : null;
			this.childrenKey.split('.').forEach(x => {
				if (_data) {
					_data = _data[x];
				} else {
					console.warn('Structure hatası');
				}
			});
			return _data;
		}
		return data[this.childrenKey];
	}

	private __convertRecursiveDataByChildKey(data: Array<any>) {
		const recursiveData: Array<any> = new Array();
		Array.isArray(data) &&
			data.forEach(item => {
				const findChild = this.__getChildsWithKey(item);
				let childs: Array<any> = [];

				if (Array.isArray(findChild)) {
					findChild.forEach((t: any) => {
						childs = [...this.__convertRecursiveDataByChildKey(findChild)];
					});
				} else {
					childs = this.__convertRecursiveDataByChildKey(findChild);
				}

				recursiveData.push({
					...item,
					children: childs
				});
			});

		return recursiveData;
	}

	__getNodeExpandStatus() {
		if (this.__searchValue) {
			return true;
		}
		if (
			this.__form.__fromMobile &&
			this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
		) {
			return undefined;
		}
		if (this.dataSource.name && this.notRecursive && this.hierarchyType !== 'childrenKey') {
			return false;
		}
		return undefined;
	}

	__getIsLeafStatus(item: any) {
		if (this.hierarchyType === 'parentKey' && this.hasItemExpr) {
			if (typeof item[this.hasItemExpr] === 'undefined') {
				return false;
			}
			return !item[this.hasItemExpr];
		}

		if (this.hierarchyType === 'childrenKey') {
			const childs = item[this.childrenKey];
			if (typeof childs === 'undefined') {
				return true;
			} else if (Array.isArray(childs) && !childs.length) {
				return true;
			}
			return false;
		}
	}

	__getTreeViewItems(data: Array<any>, childrenKey: string = 'children', expandedKeys?: any[]) {
		if (Array.isArray(data)) {
			const currentlySelectedKeys = [];

			if (this.selection?.mode === 'single' || this.selection?.type === 'rowselect') {
				currentlySelectedKeys.push(...this.getSelectedKeys());
			} else if (this.selection?.type === 'checkbox') {
				currentlySelectedKeys.push(...this.getCheckedKeys());
			}

			return data.map(i => {
				const treeItem = {};

				treeItem['children'] = this.__getTreeViewItems(
					i[childrenKey],
					childrenKey,
					expandedKeys
				);
				treeItem['text'] = this.__getTextByDisplayExpression(data, i);
				treeItem['parentKey'] = i[this.parentKey];
				treeItem['key'] = i[this.dataSource.valueExpr];
				treeItem['isLeaf'] = this.__getNodeExpandStatus() || this.__getIsLeafStatus(i);
				treeItem['expand'] = expandedKeys?.includes(i[this.dataSource.valueExpr]) || false;

				if (this.selection?.mode === 'single' || this.selection?.type === 'rowselect') {
					treeItem['selected'] = currentlySelectedKeys.some(
						key => treeItem['key'] === key
					);
				} else if (this.selection?.type === 'checkbox') {
					treeItem['checked'] = currentlySelectedKeys.some(
						key => treeItem['key'] === key
					);
				}

				return treeItem;
			});
		}

		console.log('Invalid data type ');
		return [];
	}

	__getTreeDataWithoutRelations(data: Array<any>) {
		return data.map(item => ({
			children: null,
			parentKey: null,
			key: item[this.dataSource.valueExpr],
			text: this.__getTextByDisplayExpression(data, item)
		}));
	}

	__onSearch(value: string, expandedKeys?: string[]) {
		if (this.__searchValue !== value) {
			this.__searchValue = value;
			this.__proxyChanging.treeItems = this.setExpandedKeys(this.treeItems, expandedKeys);
		}
	}

	__onLoad({ externalData, reload, forceRefresh }: IOnLoadArgs) {
		if (!this.__isInitialized && this.dataSource?.parameters?.length && !reload) {
			return;
		}

		if (
			this.__form.serviceUrl &&
			this.dataSourceType === 'dynamic' &&
			this.dataSource.valueExpr &&
			this.dataSource.displayExpr &&
			!this.dataSource.runAtServer
		) {
			this.__proxyChanging.loading = true;
			getDataSourceData({
				dataSource: this.dataSource,
				projectName: this.__form.projectName,
				clientCacheId: this.__form.__windowId,
				fieldParams: externalData?.fieldParams,
				reload,
				loadOptions: { pagination: { skip: 0, take: 9999 }, sorts: null, filters: [] },
				forceRefresh,
				projectVersion: this.__form.packageVersion
			})
				.then(response => {
					if (response?.data?.count || response?.data?.result.length) {
						const tId = this.__form.beginTransaction();
						const items = this.__overrideTreeItems(
							response.data.result,
							this.__isInitialized ? this.getExpandedKeys(this.treeItems) : undefined
						);

						this.__proxyChanging.treeItems = this.__expandAll
							? this.setAllExpandedKeys(items, false)
							: items;

						this.__form.endTransaction(tId).then(() => {
							this.__proxyChanging.loading = false;
							this.__dataSourceLoadedAtLeastOnce = true;

							this.emit('event:OnDataLoad', {
								data: response.data.result
							} as IDataLoadEventArgs);
						});
					} else {
						this.__proxyChanging.loading = false;
					}
				})
				.catch(() => {
					this.__proxyChanging.loading = false;
				});
		}
	}

	private __overrideTreeItems(data: any[], expandedKeys: any[] = []) {
		if (this.hierarchyType === 'parentKey' && this.parentKey !== '') {
			const treeData =
				this.notRecursive && !this.__searchValue ? this.__convertRecursiveData(data) : data;
			return this.__getTreeViewItems(treeData, undefined, expandedKeys);
		} else if (this.hierarchyType === 'childrenKey' && this.childrenKey !== '') {
			const treeData =
				this.notRecursive && !this.__searchValue ? this.__convertRecursiveData(data) : data;
			return this.__getTreeViewItems(treeData, this.childrenKey, expandedKeys);
		} else if (_isEmpty(this.parentKey) && _isEmpty(this.childrenKey)) {
			return this.__getTreeDataWithoutRelations(data);
		}
		return this.__getTreeItemsByRelations(data, expandedKeys); // BreakingChange oluşturmamak için eski koşullar kaldırılmamalı.
	}

	private __getTreeItemsByRelations(data: any[], expandedKeys: any[] = []) {
		let treeItems = [];
		if (_isEmpty(this.parentKey) && _isEmpty(this.childrenKey)) {
			treeItems = this.__getTreeDataWithoutRelations(data);
		} else if (this.notRecursive) {
			if (!_isEmpty(this.parentKey) && _isEmpty(this.childrenKey)) {
				treeItems = this.__getTreeViewItems(
					this.__convertRecursiveData(data),
					undefined,
					expandedKeys
				);
			} else if (_isEmpty(this.parentKey) && !_isEmpty(this.childrenKey)) {
				treeItems = this.__getTreeViewItems(
					this.__convertRecursiveDataByChildKey(data),
					undefined,
					expandedKeys
				);
			}
		} else if (
			(!_isEmpty(this.parentKey) && _isEmpty(this.childrenKey)) ||
			(_isEmpty(this.parentKey) && !_isEmpty(this.childrenKey))
		) {
			treeItems = this.__getTreeViewItems(data, this.childrenKey, expandedKeys);
		}

		return treeItems;
	}

	private __getTextByDisplayExpression(data: any[], item: any) {
		let displayFormat = this.dataSource.displayFormat;

		if (displayFormat.includes('.__userCulture')) {
			displayFormat = displayFormat.replace(
				'.__userCulture',
				`['${this.__form.currentLanguage.culture}']`
			);
		}

		return liquidEngine.parseAndRenderSync(displayFormat, item);
	}

	reload(forceRefresh?: boolean) {
		if (this.dataSource?.name) {
			if (this.dataSource?.parameters.length) {
				this.__reloadDataSource(this.dataSource, forceRefresh).catch(() => {
					this.__proxyChanging.loading = false;
				});
			} else {
				this.__onLoad({ reload: true, forceRefresh });
			}
		}
	}

	__getCheckedItems(data: ITreeviewItem<any>[], selectedItems: ITreeviewItem<any>[]): any {
		if (data?.length) {
			data.forEach(i => {
				if (i.checked) {
					selectedItems.push(i);
				}
				if (i.children) {
					this.__getCheckedItems(i.children, selectedItems);
				}
			});
		}
	}

	__setCheckItems(
		data: ITreeviewItem<any>[],
		selectedItems: ITreeviewItem<any>[],
		checkedKeys: string[]
	): any {
		if (data?.length) {
			data.forEach(i => {
				if (checkedKeys.indexOf(i.key) !== -1) {
					selectedItems.push({ ...i, checked: true });
				}
				if (i.children) {
					this.__setCheckItems(i.children, selectedItems, checkedKeys);
				}
			});
		}
	}

	__getSelectedItems(data: ITreeviewItem<any>[], selectedItems: ITreeviewItem<any>[]): any {
		if (data?.length) {
			data.forEach(i => {
				if (i.selected) {
					selectedItems.push(i);
				}
				if (i.children) {
					this.__getSelectedItems(i.children, selectedItems);
				}
			});
		}
	}

	__getItemsMobile(
		data: ITreeviewItem<any>[],
		selectedItems: ITreeviewItem<any>[],
		type: string
	): any {
		let selectedItemMobile;
		if (selectedItems?.length && data?.length) {
			if (typeof data?.[0].key == 'number') {
				selectedItemMobile =
					selectedItems?.map(i => ({ ...i, key: parseInt(i?.key) })) || [];
			} else {
				selectedItemMobile =
					selectedItems?.map(i => ({ ...i, key: i?.key.toString() })) || [];
			}
		}
		return data.map(items => ({
			...items,
			checked:
				type === 'checkbox'
					? selectedItemMobile?.findIndex(item => item.key === items.key) > -1
					: false,
			selected:
				type === 'checkbox'
					? false
					: selectedItemMobile?.findIndex(item => item.key === items.key) > -1,
			children: items.children?.length
				? this.__getItemsMobile(items.children, selectedItemMobile, type)
				: []
		}));
	}

	setCheckedKeys(treeItems: ITreeviewItem<any>[], checkedKeys: string[]) {
		return treeItems.map(item => ({
			...item,
			checked: checkedKeys.findIndex(i => i === item.key) > -1,
			children: item.children?.length ? this.setCheckedKeys(item.children, checkedKeys) : []
		}));
	}

	setExpandedKeys(treeItems: ITreeviewItem<any>[], expandedKeys: string[]) {
		return treeItems.map(item => ({
			...item,
			expand: expandedKeys.findIndex(i => i === item.key) > -1,
			children: item.children?.length ? this.setExpandedKeys(item.children, expandedKeys) : []
		}));
	}

	setAllExpandedKeys(treeItems: ITreeviewItem<any>[], state: boolean) {
		return treeItems.map(item => ({
			...item,
			expand: !state,
			children: item.children?.length ? this.setAllExpandedKeys(item.children, state) : []
		}));
	}

	onExpand(expanded: ITreeviewExpandEventArgs) {
		const _treeItems = this.setExpandedKeys(this.treeItems, expanded.expandedKeys);
		this.__proxyChanging.treeItems = _treeItems;
	}

	onCollapseTree(state: boolean) {
		this.__expandAll = !state;
		this.__proxyChanging.treeItems = this.setAllExpandedKeys(this.treeItems, state);
	}

	getExpandedKeys(items: ITreeviewItem<unknown>[]) {
		const expandedKeys: string[] = [];

		for (const item of items) {
			if (item.expand) {
				expandedKeys.push(item.key);
			}
			if (item.children?.length) {
				expandedKeys.push(...this.getExpandedKeys(item.children));
			}
		}

		return expandedKeys;
	}

	onCheck(checkedKeys: string[]) {
		const selectedItems: ITreeviewItem<any>[] = [];
		this.__setCheckItems(this.treeItems, selectedItems, checkedKeys);

		this.__proxyChanging.selectedItems = selectedItems;
		this.__proxyChanging.treeItems = this.setCheckedKeys(this.treeItems, checkedKeys);
	}

	getCheckedKeys() {
		const isNumber = typeof this.selectedItems?.[0]?.key === 'number';

		if (isNumber) {
			return this.selectedItems?.map(i => +i.key);
		}
		return this.selectedItems?.map(i => i.key);
	}

	getTreeItemByKey(key: string, treeItems: Array<ITreeviewItem<any>>) {
		let selectedItem: ITreeviewItem<any> = null;

		for (const item of treeItems) {
			if (item.key === key) {
				selectedItem = item;
				break;
			}

			const found = this.getTreeItemByKey(key, item.children || []);
			if (found) {
				selectedItem = found;
				break;
			}
		}

		return selectedItem;
	}

	onChangeSelectedItems(args: ITreeviewSelectEventArgs) {
		let selectedItems: ITreeviewItem<any>[] = [];
		const isMobileView = !Object.keys(args).some(key => key == 'item');
		if (this.__form.__fromMobile && this.__form.__mobileFormView === 3 && isMobileView) {
			this.__isMobileView = true;
			if (typeof this.treeItems?.[0].key == 'number') {
				selectedItems =
					args.selectedItems?.map(i => ({ ...i, key: parseInt(i.key) })) || [];
			} else {
				selectedItems =
					args.selectedItems?.map(i => ({ ...i, key: i.key.toString() })) || [];
			}
		} else {
			selectedItems = args.selectedItems?.map(i => ({ ...i.dataRef, children: [] })) || [];
			args.selectedKeys?.forEach(key => {
				if (!selectedItems.some(item => item.key === key)) {
					const found = this.getTreeItemByKey(key, this.treeItems);
					if (found) {
						selectedItems.push(found);
					}
				}
			});
		}

		this.__proxyChanging.selectedItems = selectedItems;
		this.__proxyChanging.treeItems = this.__setSelectedToTreeItems(
			[...this.treeItems],
			selectedItems
		);
	}

	getSelectedKeys() {
		const isNumber = typeof this.treeItems?.[0]?.key === 'number';

		if (isNumber) {
			return this.selectedItems?.map(i => +i.key);
		}
		return this.selectedItems?.map(i => i.key);
	}

	__setSelectedToTreeItems(
		treeItems: Array<ITreeviewItem<any>>,
		selectedItems: Array<ITreeviewItem<any>>
	): Array<any> {
		return treeItems.map(item => ({
			...item,
			selected: selectedItems.findIndex(i => i.key === item.key) > -1,
			children: item.children?.length
				? this.__setSelectedToTreeItems(item.children, selectedItems)
				: []
		}));
	}

	__onRightClick(args: ITreeviewRightClickEventArgs) {
		this.__onShowContextMenu(args.item, args.data);
	}
	__render(children?: JSX.Element) {
		return super.__render(
			<BCTreeview
				multiple={this.selection?.mode === 'multiple'}
				checkable={
					this.selection?.mode === 'multiple' &&
					this.selection?.type === 'checkbox' &&
					!this.__isReadOnly()
				}
				selectable={
					(this.selection?.mode === 'single' || this.selection?.type === 'rowselect') &&
					!this.__isReadOnly()
				}
				items={this.treeItems}
				onCheck={this.onCheck}
				onExpand={this.onExpand}
				onCollapseTree={this.onCollapseTree}
				expandedKeys={this.getExpandedKeys(this.treeItems)}
				checkedKeys={
					this.selection?.mode === 'multiple' && this.selection?.type === 'checkbox'
						? this.getCheckedKeys()
						: []
				}
				selectedKeys={
					this.selection?.mode === 'single' || this.selection?.type === 'rowselect'
						? this.getSelectedKeys()
						: []
				}
				onSelect={this.onChangeSelectedItems}
				disableToolbar={!this.showSearch}
				searchable={this.showSearch}
				className={Styles.treeViewStyle}
				themeClass={Styles.treeContainerStyle}
				readOnly={this.__isReadOnly()}
				onRightClick={this.__onRightClick}
				fromMobile={this.__form.__fromMobile}
				onSearch={this.__onSearch}
				showRefreshButton={!!this.dataSource?.name}
				onClickRefreshButton={() => this.reload(true)}
				disabled={!this.enabled || !this.clientEnabled}
				style={this.style}
			/>
		);
	}
}
